
export const neighborhoodCategories = [
  { id: 'all', label: 'All Districts', icon: 'ri-layout-grid-line' },
  { id: 'dining', label: 'Dining', icon: 'ri-restaurant-line' },
  { id: 'shopping', label: 'Shopping', icon: 'ri-shopping-bag-line' },
  { id: 'wellness', label: 'Wellness', icon: 'ri-heart-pulse-line' },
  { id: 'entertainment', label: 'Entertainment', icon: 'ri-film-line' },
  { id: 'services', label: 'Services', icon: 'ri-briefcase-line' },
];

export interface Business {
  name: string;
  type: string;
  rating: number;
  reviews: number;
  icon: string;
}

export interface Neighborhood {
  id: number;
  name: string;
  tagline: string;
  description: string;
  accentColor: string;
  accentBg: string;
  accentText: string;
  businessCount: number;
  walkScore: number;
  transitScore: number;
  diningCount: number;
  image: string;
  mapQuery: string;
  highlights: string[];
  topBusinesses: Business[];
  primaryCategory: string;
}

export const neighborhoods: Neighborhood[] = [
  {
    id: 1,
    name: 'Historic Old Town',
    tagline: 'Arts & Culture Hub',
    description: 'The heart of Roseville with charming boutiques, craft breweries, antique shops, and a thriving arts scene along Vernon Street and the historic railroad district.',
    accentColor: '#E07A5F',
    accentBg: 'bg-orange-50',
    accentText: 'text-orange-600',
    businessCount: 85,
    walkScore: 88,
    transitScore: 72,
    diningCount: 34,
    image: 'https://public.readdy.ai/ai/img_res/89075dd0920cc7a07ae02d859116dec1.jpg',
    mapQuery: 'Historic+Old+Town+Roseville+CA',
    highlights: ['Art Galleries', 'Craft Breweries', 'Antique Shops', 'Live Music'],
    topBusinesses: [
      { name: 'Monk\'s Cellar', type: 'Brewery & Restaurant', rating: 4.7, reviews: 892, icon: 'ri-goblet-line' },
      { name: 'Blue Line Arts', type: 'Art Gallery', rating: 4.9, reviews: 234, icon: 'ri-palette-line' },
      { name: 'Roseville Pottery', type: 'Artisan Shop', rating: 4.8, reviews: 156, icon: 'ri-store-2-line' },
      { name: 'The Owl Club', type: 'Bar & Lounge', rating: 4.5, reviews: 678, icon: 'ri-goblet-line' },
    ],
    primaryCategory: 'entertainment',
  },
  {
    id: 2,
    name: 'Westfield Galleria District',
    tagline: 'Premier Shopping & Dining',
    description: 'Northern California\'s premier shopping destination featuring 240+ stores, upscale dining, entertainment venues, and the iconic Westfield Galleria at Roseville.',
    accentColor: '#2D6A4F',
    accentBg: 'bg-emerald-50',
    accentText: 'text-emerald-600',
    businessCount: 240,
    walkScore: 76,
    transitScore: 68,
    diningCount: 52,
    image: 'https://readdy.ai/api/search-image?query=Modern%20upscale%20outdoor%20shopping%20mall%20with%20luxury%20retail%20stores%2C%20palm%20trees%2C%20clean%20walkways%2C%20shoppers%20enjoying%20sunny%20day%2C%20contemporary%20architecture%20with%20glass%20facades%2C%20Northern%20California%20shopping%20center%2C%20wide%20angle%20exterior%20photography&width=600&height=400&seq=nbhood002&orientation=landscape',
    mapQuery: 'Westfield+Galleria+Roseville+CA',
    highlights: ['240+ Stores', 'Fine Dining', 'Movie Theater', 'Luxury Brands'],
    topBusinesses: [
      { name: 'The Cheesecake Factory', type: 'Restaurant', rating: 4.3, reviews: 2341, icon: 'ri-restaurant-line' },
      { name: 'Nordstrom', type: 'Department Store', rating: 4.5, reviews: 1876, icon: 'ri-shopping-bag-line' },
      { name: 'Century Theatres', type: 'Cinema', rating: 4.4, reviews: 1234, icon: 'ri-film-line' },
      { name: 'Apple Store', type: 'Electronics', rating: 4.6, reviews: 987, icon: 'ri-smartphone-line' },
    ],
    primaryCategory: 'shopping',
  },
  {
    id: 3,
    name: 'Creekside Town Center',
    tagline: 'Family-Friendly Hub',
    description: 'A vibrant mixed-use center in West Roseville with family restaurants, fitness studios, medical offices, and everyday essentials surrounded by new residential communities.',
    accentColor: '#457B9D',
    accentBg: 'bg-sky-50',
    accentText: 'text-sky-600',
    businessCount: 65,
    walkScore: 72,
    transitScore: 55,
    diningCount: 18,
    image: 'https://readdy.ai/api/search-image?query=Modern%20suburban%20town%20center%20with%20family-friendly%20restaurants%20and%20shops%2C%20outdoor%20seating%20areas%2C%20families%20walking%20on%20clean%20sidewalks%2C%20contemporary%20retail%20architecture%2C%20green%20landscaping%2C%20sunny%20California%20day%2C%20wide%20angle%20photography&width=600&height=400&seq=nbhood003&orientation=landscape',
    mapQuery: 'Creekside+Town+Center+Roseville+CA',
    highlights: ['Family Dining', 'Fitness Studios', 'Medical Offices', 'Pet Friendly'],
    topBusinesses: [
      { name: 'Orangetheory Fitness', type: 'Fitness Studio', rating: 4.8, reviews: 456, icon: 'ri-heart-pulse-line' },
      { name: 'Panera Bread', type: 'Bakery & Cafe', rating: 4.2, reviews: 789, icon: 'ri-cake-line' },
      { name: 'Sutter Health', type: 'Medical Center', rating: 4.4, reviews: 345, icon: 'ri-hospital-line' },
      { name: 'Nothing Bundt Cakes', type: 'Bakery', rating: 4.9, reviews: 567, icon: 'ri-cake-2-line' },
    ],
    primaryCategory: 'wellness',
  },
  {
    id: 4,
    name: 'Douglas Boulevard Corridor',
    tagline: 'Professional Services Strip',
    description: 'Roseville\'s main commercial artery connecting Granite Bay to downtown, lined with professional offices, specialty restaurants, auto dealerships, and service businesses.',
    accentColor: '#6D597A',
    accentBg: 'bg-fuchsia-50',
    accentText: 'text-fuchsia-600',
    businessCount: 120,
    walkScore: 58,
    transitScore: 48,
    diningCount: 28,
    image: 'https://readdy.ai/api/search-image?query=Wide%20commercial%20boulevard%20with%20professional%20office%20buildings%2C%20specialty%20restaurants%2C%20mature%20oak%20trees%20lining%20the%20road%2C%20clean%20modern%20signage%2C%20Northern%20California%20suburban%20commercial%20district%2C%20warm%20afternoon%20light%2C%20wide%20angle%20street%20photography&width=600&height=400&seq=nbhood004&orientation=landscape',
    mapQuery: 'Douglas+Boulevard+Roseville+CA',
    highlights: ['Auto Mile', 'Professional Offices', 'Specialty Dining', 'Banking'],
    topBusinesses: [
      { name: 'LaBou Bakery Cafe', type: 'Bakery & Cafe', rating: 4.5, reviews: 1023, icon: 'ri-cup-line' },
      { name: 'Roseville Automall', type: 'Auto Dealerships', rating: 4.3, reviews: 2156, icon: 'ri-car-line' },
      { name: 'Sushi Omakase', type: 'Japanese Restaurant', rating: 4.7, reviews: 678, icon: 'ri-restaurant-line' },
      { name: 'Wells Fargo Center', type: 'Financial Services', rating: 4.1, reviews: 234, icon: 'ri-bank-line' },
    ],
    primaryCategory: 'services',
  },
  {
    id: 5,
    name: 'Fountains at Roseville',
    tagline: 'Outdoor Lifestyle Center',
    description: 'An open-air lifestyle center featuring popular chain restaurants, specialty retailers, entertainment options, and a beautiful fountain plaza perfect for evening strolls.',
    accentColor: '#E09F3E',
    accentBg: 'bg-amber-50',
    accentText: 'text-amber-600',
    businessCount: 55,
    walkScore: 70,
    transitScore: 60,
    diningCount: 22,
    image: 'https://readdy.ai/api/search-image?query=Beautiful%20outdoor%20lifestyle%20shopping%20center%20with%20decorative%20water%20fountains%2C%20open-air%20walkways%2C%20restaurant%20patios%20with%20umbrellas%2C%20modern%20retail%20architecture%2C%20evening%20golden%20hour%20lighting%2C%20people%20enjoying%20outdoor%20dining%2C%20wide%20angle%20photography&width=600&height=400&seq=nbhood005&orientation=landscape',
    mapQuery: 'Fountains+at+Roseville+CA',
    highlights: ['Outdoor Dining', 'Fountain Plaza', 'Evening Events', 'Boutiques'],
    topBusinesses: [
      { name: 'BJ\'s Restaurant', type: 'Restaurant & Brewhouse', rating: 4.3, reviews: 1567, icon: 'ri-restaurant-line' },
      { name: 'REI Co-op', type: 'Outdoor Gear', rating: 4.7, reviews: 890, icon: 'ri-compass-3-line' },
      { name: 'Yard House', type: 'Restaurant & Bar', rating: 4.4, reviews: 1234, icon: 'ri-goblet-line' },
      { name: 'Pottery Barn', type: 'Home Furnishings', rating: 4.5, reviews: 456, icon: 'ri-home-smile-line' },
    ],
    primaryCategory: 'dining',
  },
  {
    id: 6,
    name: 'Sierra Vista Crossing',
    tagline: 'Everyday Essentials',
    description: 'A convenient neighborhood center in South Roseville with grocery stores, pharmacies, quick-service restaurants, and essential services for daily living.',
    accentColor: '#BC4749',
    accentBg: 'bg-red-50',
    accentText: 'text-red-500',
    businessCount: 45,
    walkScore: 65,
    transitScore: 50,
    diningCount: 14,
    image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20neighborhood%20shopping%20center%20with%20grocery%20store%2C%20pharmacy%2C%20coffee%20shops%2C%20well-maintained%20parking%20area%2C%20suburban%20California%20setting%2C%20families%20shopping%2C%20bright%20sunny%20day%2C%20contemporary%20retail%20architecture%2C%20wide%20angle%20photography&width=600&height=400&seq=nbhood006&orientation=landscape',
    mapQuery: 'Sierra+Vista+Crossing+Roseville+CA',
    highlights: ['Grocery Stores', 'Pharmacies', 'Quick Dining', 'Convenience'],
    topBusinesses: [
      { name: 'Trader Joe\'s', type: 'Grocery Store', rating: 4.8, reviews: 2345, icon: 'ri-shopping-cart-line' },
      { name: 'Starbucks Reserve', type: 'Coffee Shop', rating: 4.5, reviews: 987, icon: 'ri-cup-line' },
      { name: 'CVS Pharmacy', type: 'Pharmacy', rating: 4.1, reviews: 567, icon: 'ri-medicine-bottle-line' },
      { name: 'Chipotle', type: 'Fast Casual', rating: 4.2, reviews: 1123, icon: 'ri-restaurant-line' },
    ],
    primaryCategory: 'services',
  },
  {
    id: 7,
    name: 'East Roseville',
    tagline: 'Parks & Community Living',
    description: 'A scenic residential corridor in East Roseville featuring neighborhood parks, local eateries, craft coffee shops, and community-focused businesses along Oak Ridge and Sunrise Avenue.',
    accentColor: '#D4813B',
    accentBg: 'bg-orange-50',
    accentText: 'text-orange-500',
    businessCount: 72,
    walkScore: 74,
    transitScore: 58,
    diningCount: 26,
    image: 'https://readdy.ai/api/search-image?query=Scenic%20residential%20neighborhood%20in%20Northern%20California%20with%20tree-lined%20streets%2C%20neighborhood%20park%20with%20green%20grass%20and%20mature%20oak%20trees%2C%20local%20shops%20and%20cafes%20along%20a%20quiet%20street%2C%20warm%20golden%20hour%20sunlight%2C%20families%20walking%20dogs%2C%20charming%20suburban%20community%20atmosphere%2C%20wide%20angle%20photography&width=600&height=400&seq=nbhood007&orientation=landscape',
    mapQuery: 'East+Roseville+CA',
    highlights: ['Community Parks', 'Local Eateries', 'Craft Coffee', 'Family Friendly'],
    topBusinesses: [
      { name: 'Sunrise Coffee Roasters', type: 'Coffee Shop', rating: 4.8, reviews: 412, icon: 'ri-cup-line' },
      { name: 'Oak Ridge Grill', type: 'American Restaurant', rating: 4.6, reviews: 687, icon: 'ri-restaurant-line' },
      { name: 'East Side Fitness', type: 'Gym & Fitness', rating: 4.7, reviews: 298, icon: 'ri-heart-pulse-line' },
      { name: 'Roseville Pet Hospital', type: 'Veterinary Clinic', rating: 4.9, reviews: 345, icon: 'ri-bear-smile-line' },
    ],
    primaryCategory: 'dining',
  },
];
